<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="$t('rjm.subTitle')" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack class="me-2" href="/master" data-me="rjm-list-button-back" />
          <McButton
            variant="secondary"
            class="me-2"
            icon="icon-outline_volume-up"
            :text="$t('rjm.join') + ' Audio'"
            @click="modalListVoice()"
            v-if="hasAccessAll(['d9a-default-voice-announcer:read'])"
            data-me="rjm-list-button-join-audio"
          />
          <McButton
            icon="icon-outline_plus"
            :text="$t('global.create')"
            @click="$router.push({ name: 'Master RJM Create' })"
            v-if="!noData && hasAccessAll(['d9a-default-voice-announcer:create'])"
            data-me="rjm-list-button-create"
          />
        </div>
      </div>
    </div>
    <McAlert v-if="!noData" variant="info" class="mb-3 w-50" withIcon :text="$t('rjm.alertText')" />
    <McGrapqlTable
      ref="rjmTable"
      :apiClient="query"
      :fields="fields"
      :customHandlers="customHandlers"
      :hasDelete="false"
      :isLoading="isLoading"
      :isEmpty="noData"
      trackBy="audioAssignmentId"
      :hasCheckbox="false"
      searchable
      withCard
      bulkDeleteApi
    >
      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          @setColumn="handleSetDataFields"
          data-me="rjm-list-filter-select"
        />
      </template>

      <template #emptyState>
        <McEmptyState
          class="pb-4"
          :title="$t('maintenance.nolist.noData')"
          :desc="$t('master.service.emptyState.desc')"
          @buttonPrimaryAction="customHandlers.handleAddEmpty"
          data-me="rjm-list-empty-state-button"
        />
      </template>
      <template #cell(audioFile.label)="{ item }">
        <div class="d-flex align-items-center justify-content-between">
          {{ item.audioFile ? item.audioFile.label : '-' }}
          <McButton
            v-if="item.audioFile && item.audioFile.audioFileUrl"
            variant="secondary"
            class="ms-2"
            size="small"
            icon="icon-outline_volume-up"
            text=""
            @click="modalPlayVoice(item.audioFile)"
            data-me="rjm-list-button-play-audio"
          />
        </div>
      </template>
      <template #cell(assignmentValue)="{ item }">
        {{
          item.assignmentValue === 'enter'
            ? $t('rjm.enter')
            : item.assignmentValue === 'exit'
            ? $t('rjm.exit')
            : item.assignmentValue
        }}
        {{ $t('rjm.location') }}
      </template>
      <template #cell(geofences)="{ item }">
        <div class="d-flex flex-wrap gap-2">
          <template v-if="Array.isArray(item.geofences) && item.geofences.length">
            <span
              v-for="g in item.geofences.slice(0, 3)"
              :key="g.geofenceId"
              class="btn btn-primary mc-button mc-btn__small"
              data-me="rjm-list-geofence-item"
            >
              {{ g.fenceName }}
            </span>
            <span
              v-if="item.geofences.length > 3"
              :id="`geofence-tooltip-${item.audioAssignmentId}`"
              class="mc-button mc-btn__small bg-green-500 text-white"
              data-me="rjm-list-geofence-more"
            >
              +{{ item.geofences.length - 3 }}
            </span>
            <b-tooltip
              v-if="item.geofences.length > 3"
              :target="`geofence-tooltip-${item.audioAssignmentId}`"
              placement="top"
            >
              {{
                item.geofences
                  .slice(3)
                  .map((g) => g.fenceName)
                  .join(', ')
              }}
            </b-tooltip>
          </template>
        </div>
      </template>
      <template #cell(vehicles)="{ item }">
        <div class="d-flex flex-wrap gap-2">
          <template v-if="Array.isArray(item.vehicles) && item.vehicles.length">
            <span
              v-for="v in item.vehicles.slice(0, 3)"
              :key="v.id"
              class="btn btn-primary mc-button mc-btn__small"
              data-me="rjm-list-vehicle-item"
            >
              {{ v.licensePlate }}
            </span>
            <span
              v-if="item.vehicles.length > 3"
              :id="`vehicle-tooltip-${item.audioAssignmentId}`"
              class="mc-button mc-btn__small bg-green-500 text-white"
              data-me="rjm-list-vehicle-more"
            >
              +{{ item.vehicles.length - 3 }}
            </span>
            <b-tooltip
              v-if="item.vehicles.length > 3"
              :target="`vehicle-tooltip-${item.audioAssignmentId}`"
              placement="top"
            >
              {{
                item.vehicles
                  .slice(3)
                  .map((v) => v.licensePlate)
                  .join(', ')
              }}
            </b-tooltip>
          </template>
          <template v-else>
            <span
              class="btn btn-primary mc-button mc-btn__small"
              data-me="rjm-list-all-supported-vehicles"
              >{{ $t('rjm.allSupportedVehicles') }}</span
            >
          </template>
        </div>
      </template>
      <template #cell(configuration)="{ item }">
        <div class="text-center configuration">
          <McButton
            :variant="
              item.successfulAudioIssuance === item.totalAudioIssuance ? 'success' : 'warning'
            "
            size="small"
            :text="`${item.successfulAudioIssuance || 0}/${item.totalAudioIssuance || 0}`"
            @click.native="showModalListFleet(item)"
            data-me="rjm-list-button-configuration"
            icon="icon-fill_arrow-ios-right"
            icon-position="right"
          />
        </div>
      </template>
      <template #cell(action)="{ item }">
        <McButton
          variant="secondary-red"
          icon="icon-outline_trash"
          size="small"
          text=""
          @click="confirmDelete(item)"
          data-me="rjm-list-button-delete"
        />
      </template>
    </McGrapqlTable>
    <ModalPlayVoice ref="modalPlayVoice" />
    <ModalListVoice ref="modalListVoice" />
    <ModalListFleet ref="modalListFleet" />
    <b-modal
      id="modal-confirm-delete"
      ref="modal-confirm-delete"
      :title="$t('global.action.delete')"
      centered
      @ok="handleConfirmDelete"
      data-me="rjm-list-modal-confirm-delete"
    >
      <p v-if="selectedItem">
        {{ $t('global.confirmDelete1') }} <strong>{{ selectedItem.label }}</strong> ?
      </p>
      <template #modal-footer="{ ok, cancel }">
        <McButton
          variant="secondary"
          :text="$t('global.action.cancel')"
          @click="cancel()"
          data-me="rjm-list-modal-confirm-delete-button-cancel"
        />
        <McButton
          variant="primary"
          :text="$t('global.action.delete')"
          @click="ok()"
          data-me="rjm-list-modal-confirm-delete-button-confirm"
        />
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McEmptyState from '@/components/McEmptyState'
import ModalPlayVoice from '@/router/views/master/rjm/components/ModalPlayVoice'
import { Helper } from '@/mixin/helper'
import { audioAssignments, deleteAudioAssignment } from '@/services/api/master/rjm'
import { mapGetters } from 'vuex'
import ModalListVoice from './components/ModalListVoice.vue'
import McAlert from '@/components/McAlert'
import McBadge from '@/components/McBadge'
import ModalListFleet from '@/router/views/master/rjm/components/ModalListFleet'

export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McEmptyState,
    ModalPlayVoice,
    ModalListVoice,
    McAlert,
    McBadge,
    ModalListFleet,
  },
  mixins: [Helper],
  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          return this.onRowClicked(item)
        },

        handleAddEmpty: () => {
          return this.$router.push({ name: 'Master RJM Create' })
        },
      },
      isLoading: true,
      noData: true,
      selectedItem: null,
    }
  },
  computed: {
    /**
     * Total no. of records
     */
    title() {
      return 'Voice Announcer'
    },
    fields() {
      const fields = [
        {
          key: 'label',
          label: this.$t('rjm.nameAlert'),
          sortable: true,
          isRedirectable: true,
          isLocked: true,
        },
        {
          key: 'audioFile.label',
          label: this.$t('rjm.nameAudio'),
          sortable: true,
        },
        {
          key: 'assignmentValue',
          label: this.$t('rjm.condition'),
          sortable: true,
          formatter: (val) => {
            return val ? val : '-'
          },
        },
        {
          key: 'geofences',
          label: this.$t('rjm.location'),
          sortable: true,
          formatter: (val) => {
            if (!val || !Array.isArray(val) || val.length === 0) return '-'
            return val.map((item) => item.fenceName).join(', ')
          },
        },
        {
          key: 'vehicles',
          label: this.$t('rjm.vehicle'),
          sortable: true,
          formatter: (val, key, item) => {
            if (item.allSupportedVehicles) return this.$t('rjm.allSupportedVehicles')
            if (!val || !Array.isArray(val) || val.length === 0) return '-'
            return val.map((item) => item.licensePlate).join(', ')
          },
        },
        {
          key: 'configuration',
          label: 'Status ' + this.$t('rjm.configuration'),
          sortable: false,
        },
        {
          key: 'action',
          label: this.$t('global.action'),
          sortable: false,
        },
      ]

      return fields
    },
    query() {
      return {
        get: async (filter) => {
          return this.getData(filter)
        },
        delete: async (items) => {
          return this.deleteData(items)
        },
      }
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
  },
  methods: {
    async getData(filter) {
      this.isLoading = true
      try {
        const res = await this.$apollo.query({
          query: audioAssignments,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              ...filter,
              assignmentType: 'RJM_GEOFENCE',
            },
          },
        })

        if (res.data.audioAssignments.isSuccessful) {
          this.noData = res.data.audioAssignments.audioAssignments.data.length === 0
          return res.data.audioAssignments.audioAssignments
        } else {
          this.noData = true
          this.$mcToast.error(res.data.audioAssignments.message)
          return {
            data: [],
            pagination: {
              total: 0,
              page: 1,
              show: 10,
            },
          }
        }
      } catch (e) {
        this.noData = true
        this.$mcToast.error(e.message)
        return {
          data: [],
          pagination: {
            total: 0,
            page: 1,
            show: 10,
          },
        }
      } finally {
        this.isLoading = false
      }
    },
    onRowClicked(item) {
      this.$router.push({
        name: 'Master RJM Update',
        params: { id: item.audioAssignmentId },
      })
    },
    modalPlayVoice(item) {
      this.$refs.modalPlayVoice.show(item)
    },
    modalListVoice() {
      this.$bvModal.show('modal-list-voice')
    },
    showModalListFleet(item) {
      const vehicles = item?.vehicles || []
      const audioFileId = item?.audioFileId || item?.audioFile?.audioFileId || null
      this.$refs.modalListFleet.show({ vehicles, audioFileId })
    },
    confirmDelete(item) {
      this.selectedItem = item
      this.$refs['modal-confirm-delete'].show()
    },
    async handleConfirmDelete() {
      const res = await this.deleteData(this.selectedItem.audioAssignmentId)
      if (res) {
        this.$mcToast.success(this.$t('global.alert.text.delete'))
        this.$refs.rjmTable.getData({
          page: 1,
          limit: 10,
        })
      }
      this.$refs['modal-confirm-delete'].hide()
    },
    async deleteData(ids) {
      const idsArray = Array.isArray(ids) ? ids : [ids]

      try {
        for (const id of idsArray) {
          const res = await this.$apollo.mutate({
            mutation: deleteAudioAssignment,
            variables: {
              input: {
                audioAssignmentId: parseInt(id, 10),
              },
            },
          })

          if (!res.data.deleteAudioAssignment.isSuccessful) {
            throw new Error(res.data.deleteAudioAssignment.message)
          }
        }

        return true
      } catch (e) {
        this.$mcToast.error(e.message)
        return false
      }
    },
  },
}
</script>
<style lang="scss">
.configuration {
  .btn {
    width: 75px;
    justify-content: center;
  }
}
</style>
